import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { useTimezone, formatInTimezone } from '@kioskgaming/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Ban,
  History,
  KeyRound,
  Shield,
  Mail,
  Phone,
  User,
  Wallet,
  X,
  Unlink,
} from 'lucide-react';
import {
  banShopPlayer,
  closeAndReleaseShopPlayer,
  fetchPlayerTransactions,
  postShopPlayerReissueCredentials,
  unbanShopPlayer,
  sendPlayerVerification,
  verifyPlayerVerification,
} from '../../services/api';
import type { ShopWalletLedgerRow, ShopPlayer } from '../../types';
import {
  mergePlayerWalletTxMeta,
  playerWalletTxFeeLines,
  playerWalletTxPayerLine,
  playerWalletTxTitle,
} from '../../utils/playerWalletTxLabels';
import SellCreditsCashToPlayerTrigger from '../credits/SellCreditsCashToPlayerTrigger';
import RedeemCreditsToPlayerTrigger from '../credits/RedeemCreditsToPlayerTrigger';
import PlayerGameWalletsSection from './PlayerGameWalletsSection';
import ModalPortal from '../ui/ModalPortal';
import {
  BAN_PERIOD_OPTIONS,
  type BanPeriod,
  formatUnbanDisplay,
  isAdminBanActive,
  isEffectivelyBanned,
  isShopBanActive,
  previewUnbanFromPeriod,
} from '../../utils/banUtils';

type Props = {
  player: ShopPlayer;
  onChanged?: () => void;
  /** Shop wallet credits — shown in Deposit modal when set */
  shopWallet?: { balance: number | null; loading: boolean };
  /** Open Deposit or Redeem on first paint (Home quick actions). */
  autoAction?: 'deposit' | 'redeem' | null;
};

function txTime(row: ShopWalletLedgerRow, timezone: string) {
  const v = row.created_at ?? row.createdAt;
  if (!v) return '—';
  return formatInTimezone(v, timezone);
}

function toNumber(v: unknown): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function txSignedAmount(row: ShopWalletLedgerRow): number {
  const meta = row.metadata as Record<string, unknown> | null | undefined;
  const delta = meta?.player_credit_delta;
  if (typeof delta === 'number' && Number.isFinite(delta)) return delta;

  const before = toNumber(row.balanceBefore ?? row.balance_before);
  const after = toNumber(row.balanceAfter ?? row.balance_after);
  if (Number.isFinite(before) && Number.isFinite(after)) return after - before;
  return toNumber(row.amount);
}

function fmtCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function txBalanceBeforeAfter(row: ShopWalletLedgerRow): { before: number | null; after: number | null } {
  const beforeRaw = row.balanceBefore ?? row.balance_before;
  const afterRaw = row.balanceAfter ?? row.balance_after;
  const before = beforeRaw === undefined || beforeRaw === null ? null : toNumber(beforeRaw);
  const after = afterRaw === undefined || afterRaw === null ? null : toNumber(afterRaw);
  const hasBefore = beforeRaw !== undefined && beforeRaw !== null && Number.isFinite(before);
  const hasAfter = afterRaw !== undefined && afterRaw !== null && Number.isFinite(after);
  return {
    before: hasBefore ? before : null,
    after: hasAfter ? after : null
  };
}

export default function PlayerDetailPanel({ player, onChanged, shopWallet, autoAction = null }: Props) {
  const { timezone } = useTimezone();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState<ShopWalletLedgerRow[]>([]);
  const [reissueConfirmOpen, setReissueConfirmOpen] = useState(false);
  const [reissueBusy, setReissueBusy] = useState(false);
  const [reissueResult, setReissueResult] = useState<{
    username: string;
    tempPassword: string;
    phone: string | null;
    email: string | null;
  } | null>(null);

  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyType, setVerifyType] = useState<'phone' | 'email'>('phone');
  const [verifyOtp, setVerifyOtp] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState<'phone' | 'email' | null>(null);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banPeriod, setBanPeriod] = useState<BanPeriod | ''>('');
  const [banBusy, setBanBusy] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closeConfirm, setCloseConfirm] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);

  const shopBanActive = isShopBanActive(player);
  const adminBanActive = isAdminBanActive(player);
  const isClosed = player.status === 'terminated';
  const isBanned = isEffectivelyBanned(player) || player.status === 'banned';
  const actionsLocked = isBanned || isClosed;
  const periodPreview = previewUnbanFromPeriod(banPeriod);

  const playerBalance = useMemo(() => toNumber(player.balance), [player.balance]);

  const playerDisplayName =
    player.username || player.phone || player.email || player.id || 'Player';

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetchPlayerTransactions({ playerId: player.id, phone: player.phone, limit: 400 });
      if (res.success && Array.isArray(res.data)) {
        setHistoryRows(res.data);
      } else {
        setHistoryRows([]);
      }
    } catch {
      setHistoryRows([]);
      toast.error('Could not load Player transactions');
    } finally {
      setHistoryLoading(false);
    }
  }, [player.id, player.phone]);

  const onDepositSuccess = useCallback(() => {
    onChanged?.();
    void loadHistory();
  }, [onChanged, loadHistory]);

  const onRedeemSuccess = useCallback(() => {
    onChanged?.();
    void loadHistory();
  }, [onChanged, loadHistory]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!reissueConfirmOpen && !reissueResult) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !reissueBusy) {
        setReissueConfirmOpen(false);
        if (reissueResult) setReissueResult(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reissueConfirmOpen, reissueResult, reissueBusy]);

  useEffect(() => {
    if (!reissueConfirmOpen && !reissueResult) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [reissueConfirmOpen, reissueResult]);

  useEffect(() => {
    if (!verifyModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !verifyBusy) {
        setVerifyModalOpen(false);
        setVerifyOtp('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [verifyModalOpen, verifyBusy]);

  const submitBanAction = async () => {
    setBanBusy(true);
    try {
      if (shopBanActive) {
        const res = await unbanShopPlayer(player.id);
        if (!res.success) {
          toast.error(res.message || 'Could not remove Shop ban');
          return;
        }
        toast.success(
          adminBanActive
            ? 'Shop ban removed. Player remains blocked by platform restriction.'
            : 'Shop ban removed'
        );
      } else {
        const reason = banReason.trim();
        if (!reason) {
          toast.error('Enter a ban reason');
          return;
        }
        if (!banPeriod) {
          toast.error('Select a ban period');
          return;
        }
        const res = await banShopPlayer(player.id, { reason, ban_period: banPeriod });
        if (!res.success) {
          toast.error(res.message || 'Could not apply Shop ban');
          return;
        }
        toast.success('Shop ban applied');
      }
      setBanModalOpen(false);
      setBanReason('');
      setBanPeriod('');
      onChanged?.();
    } catch {
      toast.error(shopBanActive ? 'Remove Shop ban failed' : 'Apply Shop ban failed');
    } finally {
      setBanBusy(false);
    }
  };

  const submitCloseAndRelease = async () => {
    const reason = closeReason.trim();
    if (!reason) {
      toast.error('Enter a close reason');
      return;
    }
    if (closeConfirm.trim().toLowerCase() !== 'close') {
      toast.error('Type close to confirm');
      return;
    }
    setCloseBusy(true);
    try {
      const res = await closeAndReleaseShopPlayer(player.id, { reason, confirm: true });
      if (!res.success) {
        toast.error(res.message || 'Could not close Player');
        return;
      }
      toast.success(res.message || 'Player closed. Contacts released.');
      setCloseModalOpen(false);
      setCloseReason('');
      setCloseConfirm('');
      onChanged?.();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Could not close Player');
    } finally {
      setCloseBusy(false);
    }
  };

  const runReissueCredentials = async () => {
    if (isClosed) {
      toast.error('This Player is closed.');
      return;
    }
    if (isBanned) {
      toast.error('This Player is banned. Unban them first.');
      return;
    }
    setReissueBusy(true);
    try {
      const res = await postShopPlayerReissueCredentials(player.id);
      if (!res.success || !res.data?.temp_password || !res.data.username) {
        toast.error(res.message || 'Could not re-issue credentials');
        return;
      }
      setReissueConfirmOpen(false);
      setReissueResult({
        username: res.data.username,
        tempPassword: res.data.temp_password,
        phone: res.data.phone ?? null,
        email: res.data.email ?? null
      });
      toast.success('New username and password sent by SMS and/or email when available.');
      onChanged?.();
    } catch {
      toast.error('Could not re-issue credentials');
    } finally {
      setReissueBusy(false);
    }
  };

  const sendOtp = async (type: 'phone' | 'email') => {
    if (isClosed) {
      toast.error('This Player is closed.');
      return;
    }
    setSendBusy(type);
    try {
      const res = await sendPlayerVerification(player.id, type);
      if (!res.success) {
        toast.error(res.message || 'Could not send OTP');
        return;
      }
      if (res.data?.already_verified) {
        toast.success(`${type === 'phone' ? 'Phone' : 'Email'} already verified.`);
        onChanged?.();
        return;
      }
      toast.success(`OTP sent to ${type === 'phone' ? 'phone' : 'email'}.`);
    } catch {
      toast.error('Could not send OTP');
    } finally {
      setSendBusy(null);
    }
  };

  const openVerify = (type: 'phone' | 'email') => {
    if (isClosed) {
      toast.error('This Player is closed.');
      return;
    }
    setVerifyType(type);
    setVerifyOtp('');
    setVerifyModalOpen(true);
  };

  const submitVerify = async () => {
    if (isClosed) {
      toast.error('This Player is closed.');
      return;
    }
    const otp = verifyOtp.replace(/\D/g, '').slice(0, 6);
    if (otp.length !== 6) {
      toast.error('Enter a 6-digit OTP');
      return;
    }
    setVerifyBusy(true);
    try {
      const res = await verifyPlayerVerification(player.id, { type: verifyType, otp });
      if (!res.success) {
        toast.error(res.message || 'Verification failed');
        return;
      }
      toast.success(`${verifyType === 'phone' ? 'Phone' : 'Email'} verified.`);
      setVerifyModalOpen(false);
      setVerifyOtp('');
      onChanged?.();
    } catch {
      toast.error('Verification failed');
    } finally {
      setVerifyBusy(false);
    }
  };

  const initial =
    (player.username?.trim() || player.phone || '?').charAt(0).toUpperCase();

  const profileFields = [
    {
      icon: User,
      label: 'Username',
      value: player.username?.trim() || '—',
      mono: false,
    },
    {
      icon: Phone,
      label: 'Phone',
      value: player.phone || '—',
      mono: true,
    },
    {
      icon: Mail,
      label: 'Email',
      value: player.email || '—',
      mono: false,
    },
    {
      icon: Wallet,
      label: 'Balance',
      value: (player.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      mono: true,
      highlight: true,
    },
  ];

  const phoneVerified = player.phone_verified === true;
  const emailVerified = player.email_verified === true;

  return (
    <div className="space-y-8">
      <section className="overflow-hidden rounded-2xl border border-emerald-100/90 bg-white shadow-sm ring-1 ring-emerald-50">
        <div className="border-b border-emerald-100/80 bg-gradient-to-r from-emerald-50/80 via-white to-teal-50/40 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700/80">Player detail</p>
              <h2 className="mt-1 text-lg font-bold tracking-tight text-gray-900 sm:text-xl">Profile</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {isClosed ? (
                <span className="inline-flex rounded-full bg-neutral-200 px-2.5 py-1 text-xs font-semibold uppercase text-neutral-800">
                  Closed
                </span>
              ) : null}
              {isBanned ? (
                <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold uppercase text-red-800">
                  Banned
                </span>
              ) : null}
              <SellCreditsCashToPlayerTrigger
                playerId={player.id}
                playerName={playerDisplayName}
                creditBalance={shopWallet?.balance}
                creditBalanceLoading={shopWallet?.loading}
                onSuccess={onDepositSuccess}
                disabled={actionsLocked}
                defaultOpen={!actionsLocked && autoAction === 'deposit'}
              />
              <RedeemCreditsToPlayerTrigger
                playerId={player.id}
                playerName={playerDisplayName}
                playerBalance={playerBalance}
                onSuccess={onRedeemSuccess}
                disabled={actionsLocked}
                defaultOpen={!actionsLocked && autoAction === 'redeem'}
              />
              <button
                type="button"
                disabled={actionsLocked}
                onClick={() => setReissueConfirmOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
                Re-issue login
              </button>
              {!isClosed ? (
                <button
                  type="button"
                  onClick={() => {
                    setBanReason('');
                    setBanPeriod('');
                    setBanModalOpen(true);
                  }}
                  className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition ${
                    shopBanActive
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                      : 'border-red-300 bg-red-50 text-red-900 hover:bg-red-100'
                  }`}
                >
                  {shopBanActive ? (
                    <>
                      <Shield className="h-4 w-4 shrink-0" aria-hidden />
                      Remove Shop ban
                    </>
                  ) : (
                    <>
                      <Ban className="h-4 w-4 shrink-0" aria-hidden />
                      Shop ban
                    </>
                  )}
                </button>
              ) : null}
              {!isClosed ? (
                <button
                  type="button"
                  onClick={() => {
                    setCloseReason('');
                    setCloseConfirm('');
                    setCloseModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-400 bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                >
                  <Unlink className="h-4 w-4 shrink-0" aria-hidden />
                  Close & release
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {isClosed ? (
            <div
              className="mb-4 rounded-xl border border-neutral-300 bg-neutral-100 px-4 py-3 text-sm text-neutral-900"
              role="status"
            >
              <p className="font-semibold">This Player is closed</p>
              <p className="mt-1 text-neutral-700">
                Email, phone, username, and Telegram were released. The Player can register again with
                those contacts. Wallet and game history stay on this closed account.
              </p>
              {player.terminate_reason ? (
                <p className="mt-2 text-neutral-800">
                  <span className="font-medium">Reason:</span> {player.terminate_reason}
                </p>
              ) : null}
              {player.terminated_at ? (
                <p className="mt-1 text-neutral-800">
                  <span className="font-medium">Closed at:</span>{' '}
                  {formatInTimezone(player.terminated_at, timezone)}
                </p>
              ) : null}
              <p className="mt-1 text-neutral-800">
                <span className="font-medium">Closed by:</span>{' '}
                {player.terminated_by ? 'Admin' : 'This Shop'}
              </p>
            </div>
          ) : null}
          {isBanned ? (
            <div
              className="mb-4 space-y-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
              role="status"
            >
              <div>
                <p className="font-semibold">Player cannot use the kiosk</p>
                <p className="mt-1 text-red-800">
                  Deposit, redeem, and credential re-issue are disabled while any restriction below is active.
                </p>
              </div>
              {shopBanActive ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-amber-950">
                  <p className="text-xs font-semibold uppercase tracking-wide">Shop ban</p>
                  <p className="mt-1 text-sm">
                    <span className="font-semibold">Unban: </span>
                    {formatUnbanDisplay(player.shop_ban_period, player.shop_ban_until)}
                  </p>
                  {player.shop_ban_reason || player.ban_reason ? (
                    <p className="mt-1 text-xs">
                      <span className="font-semibold">Reason: </span>
                      {player.shop_ban_reason || player.ban_reason}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {adminBanActive ? (
                <div className="rounded-lg border border-red-300 bg-white/60 px-3 py-2 text-red-950">
                  <p className="text-xs font-semibold uppercase tracking-wide">Platform ban</p>
                  <p className="mt-1 text-sm">
                    <span className="font-semibold">Unban: </span>
                    {formatUnbanDisplay(player.admin_ban_period, player.admin_ban_until)}
                  </p>
                  {player.admin_ban_reason ? (
                    <p className="mt-1 text-xs">
                      <span className="font-semibold">Reason: </span>
                      {player.admin_ban_reason}
                    </p>
                  ) : null}
                  <p className="mt-2 text-xs text-red-800">Only Admin can remove this restriction.</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="flex shrink-0 items-center gap-4 lg:flex-col lg:items-center">
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-2xl font-bold text-white shadow-md ring-4 ring-emerald-100"
                aria-hidden
              >
                {initial}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="grid gap-3 sm:grid-cols-2">
                {profileFields.map(({ icon: Icon, label, value, mono, highlight }) => (
                  <div
                    key={label}
                    className={`rounded-xl border px-4 py-3 transition ${
                      highlight
                        ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/90 to-white shadow-sm'
                        : 'border-gray-100 bg-gray-50/50 hover:border-emerald-100/80'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <Icon className={`h-3.5 w-3.5 ${highlight ? 'text-emerald-600' : 'text-gray-400'}`} aria-hidden />
                      {label}
                    </div>
                    <p
                      className={`mt-1.5 break-all text-sm font-semibold text-gray-900 ${mono ? 'font-mono tabular-nums' : ''} ${
                        highlight ? 'text-emerald-900' : ''
                      }`}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Verification</p>
          <h3 className="mt-1 text-base font-bold text-gray-900">Phone & email</h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Phone className="h-4 w-4 text-gray-500" aria-hidden />
                  Phone
                </div>
                <p className="mt-1 text-xs text-gray-600">{player.phone || '—'}</p>
                <span
                  className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    phoneVerified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {phoneVerified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <button
                  type="button"
                  disabled={isClosed || !player.phone || phoneVerified || sendBusy === 'phone'}
                  onClick={() => void sendOtp('phone')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {sendBusy === 'phone' ? 'Sending…' : 'Send OTP'}
                </button>
                <button
                  type="button"
                  disabled={isClosed || !player.phone || phoneVerified}
                  onClick={() => openVerify('phone')}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Mail className="h-4 w-4 text-gray-500" aria-hidden />
                  Email
                </div>
                <p className="mt-1 text-xs text-gray-600">{player.email || 'Not set'}</p>
                <span
                  className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    player.email
                      ? emailVerified
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-900'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {player.email ? (emailVerified ? 'Verified' : 'Unverified') : 'Not set'}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <button
                  type="button"
                  disabled={isClosed || !player.email || emailVerified || sendBusy === 'email'}
                  onClick={() => void sendOtp('email')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {sendBusy === 'email' ? 'Sending…' : 'Send OTP'}
                </button>
                <button
                  type="button"
                  disabled={isClosed || !player.email || emailVerified}
                  onClick={() => openVerify('email')}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PlayerGameWalletsSection playerId={player.id} />

      {/* Transaction history — always visible */}
      <section className="rounded-2xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100 text-stone-600">
            <History className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Transaction history</h3>
            <p className="text-xs text-gray-500">Recent activity for this Player</p>
          </div>
        </div>

        {historyLoading ? (
          <div className="py-8">
            <PageLoadingIndicator variant="inline" label="Loading…" accentColor="#059669" />
          </div>
        ) : historyRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-10 text-center">
            <p className="text-sm text-gray-500">No transactions for this Player yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {historyRows.slice(0, 50).map((row) => {
              const signed = txSignedAmount(row);
              const { before: balBefore, after: balAfter } = txBalanceBeforeAfter(row);
              const meta = mergePlayerWalletTxMeta(row);
              const txTitle = playerWalletTxTitle(meta, String(row.type));
              const payerLine = playerWalletTxPayerLine(meta);
              const feeLines = playerWalletTxFeeLines(meta);
              const entryKind = String(meta.entryKind || '').toLowerCase();
              return (
                <div
                  key={row.id}
                  className="rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/80 to-white px-4 py-3 text-sm shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900">{txTitle}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{txTime(row, timezone)}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Credits</p>
                      <p
                        className={`font-semibold tabular-nums ${
                          signed >= 0 ? 'text-emerald-700' : 'text-amber-700'
                        }`}
                      >
                        {signed >= 0 ? '+' : ''}
                        {signed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                      </p>
                    </div>
                  </div>
                  {payerLine ? <p className="mt-2 text-xs leading-relaxed text-gray-700">{payerLine}</p> : null}
                  {feeLines.length > 0 ? (
                    <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-xs text-gray-700">
                      {feeLines.map((line) => (
                        <li key={line.label} className="flex flex-wrap justify-between gap-x-3 gap-y-0.5">
                          <span className="text-gray-600">{line.label}</span>
                          <span className="font-medium tabular-nums text-gray-900">{line.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {balBefore != null || balAfter != null ? (
                    <p className="mt-2 text-xs tabular-nums text-gray-600">
                      <span className="text-gray-500">Balance (before)</span>{' '}
                      <span className="font-medium text-gray-800">
                        {balBefore != null ? fmtCredits(balBefore) : '—'}
                      </span>
                      <span className="mx-1.5 text-gray-400">→</span>
                      <span className="text-gray-500">after</span>{' '}
                      <span className="font-medium text-gray-800">
                        {balAfter != null ? fmtCredits(balAfter) : '—'}
                      </span>
                    </p>
                  ) : null}
                  {entryKind === 'card_credit_from_shop' ? (
                    <p className="mt-1 text-xs text-amber-800/90">
                      Card flow: may include debt / reconciliation per Shop policy.
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {banModalOpen && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ban-player-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h2 id="ban-player-title" className="text-base font-bold text-gray-900">
              {shopBanActive ? 'Remove Shop ban?' : 'Apply Shop ban?'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {shopBanActive
                ? adminBanActive
                  ? 'Shop restriction will be removed. Player remains blocked while platform restriction is active.'
                  : 'Player can use the kiosk again if no other restriction is active.'
                : adminBanActive
                  ? 'This adds a Shop restriction. Player remains blocked until all active restrictions are cleared.'
                  : 'Player will not be able to sign in or use the kiosk while this Shop restriction is active.'}
            </p>
            {!shopBanActive ? (
              <>
                <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="shop-ban-period">
                  Ban period <span className="text-red-600">*</span>
                  <select
                    id="shop-ban-period"
                    value={banPeriod}
                    onChange={(e) => setBanPeriod((e.target.value || '') as BanPeriod | '')}
                    disabled={banBusy}
                    className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
                  >
                    <option value="">Select ban period…</option>
                    {BAN_PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                {periodPreview ? (
                  <p className="mt-1 text-xs text-gray-600">{periodPreview}</p>
                ) : null}
                {banPeriod === 'permanent' ? (
                  <p className="mt-1 text-xs text-red-700">This restriction will not end automatically.</p>
                ) : null}
                <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="shop-ban-reason">
                  Ban reason <span className="text-red-600">*</span>
                  <textarea
                    id="shop-ban-reason"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    disabled={banBusy}
                    rows={3}
                    placeholder="Enter reason for Shop ban…"
                    className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
                  />
                </label>
              </>
            ) : null}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={banBusy}
                onClick={() => setBanModalOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={banBusy || (!shopBanActive && (!banReason.trim() || !banPeriod))}
                onClick={() => void submitBanAction()}
                className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${
                  shopBanActive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {banBusy ? 'Working…' : shopBanActive ? 'Remove Shop ban' : 'Apply Shop ban'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {closeModalOpen && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-release-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-5 shadow-xl">
            <h2 id="close-release-title" className="text-base font-bold text-gray-900">
              Close & release contacts?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This cannot be undone. The account is closed. Email, phone, username, and Telegram are
              rewritten so the same contacts can register a new Player. In-game and wallet money stay
              on this closed account and are not recalled.
            </p>
            <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="close-release-reason">
              Reason <span className="text-red-600">*</span>
              <textarea
                id="close-release-reason"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                disabled={closeBusy}
                rows={3}
                placeholder="Enter reason for closing this Player…"
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50"
              />
            </label>
            <label className="mt-3 block text-sm font-medium text-gray-700" htmlFor="close-release-confirm">
              Type <span className="font-mono">close</span> to confirm <span className="text-red-600">*</span>
              <input
                id="close-release-confirm"
                value={closeConfirm}
                onChange={(e) => setCloseConfirm(e.target.value)}
                disabled={closeBusy}
                autoComplete="off"
                placeholder="close"
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm text-gray-900 shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50"
              />
            </label>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={closeBusy}
                onClick={() => setCloseModalOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  closeBusy || !closeReason.trim() || closeConfirm.trim().toLowerCase() !== 'close'
                }
                onClick={() => void submitCloseAndRelease()}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {closeBusy ? 'Closing…' : 'Close & release'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {reissueConfirmOpen && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reissue-cred-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h2 id="reissue-cred-title" className="text-base font-bold text-gray-900">
              Re-issue username and password?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              The Player gets a new username and temporary password. The old username stops working immediately. New
              credentials are sent by SMS and email when those are on file (same as creating a Player).
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={reissueBusy}
                onClick={() => setReissueConfirmOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reissueBusy}
                onClick={() => void runReissueCredentials()}
                className="rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60"
              >
                {reissueBusy ? 'Working…' : 'Confirm re-issue'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {reissueResult && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reissue-result-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-emerald-200 bg-white p-5 shadow-xl">
            <h2 id="reissue-result-title" className="text-base font-bold text-gray-900">
              New login details
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Share securely. SMS/email were queued when phone/email exist on this account.
            </p>
            <dl className="mt-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-4 text-sm">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Username</dt>
                <dd className="mt-1 font-mono font-semibold text-gray-900">{reissueResult.username}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Temporary password</dt>
                <dd className="mt-1 break-all font-mono font-semibold text-gray-900">{reissueResult.tempPassword}</dd>
              </div>
            </dl>
            <ul className="mt-3 list-inside list-disc text-xs text-gray-600">
              {reissueResult.phone ? <li>SMS: {reissueResult.phone}</li> : <li>No phone on file — SMS skipped</li>}
              {reissueResult.email ? <li>Email: {reissueResult.email}</li> : <li>No email on file — email skipped</li>}
            </ul>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setReissueResult(null)}
                className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {verifyModalOpen && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="verify-otp-title"
        >
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close"
            disabled={verifyBusy}
            onClick={() => setVerifyModalOpen(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="verify-otp-title" className="text-base font-bold text-gray-900">
                  Verify {verifyType === 'phone' ? 'phone' : 'email'}
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the 6-digit OTP sent to the Player.
                </p>
              </div>
              <button
                type="button"
                disabled={verifyBusy}
                onClick={() => setVerifyModalOpen(false)}
                className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit OTP"
                value={verifyOtp}
                onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm tracking-widest focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={verifyBusy}
                  onClick={() => setVerifyModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={verifyBusy}
                  onClick={() => void submitVerify()}
                  className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {verifyBusy ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
